import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography } from '@mui/material';
import { useTimeline } from '../TimelineContext';
import { useChart } from '../../../../components/chart';
import { friendlyNameConverter } from '../../../../services/localizationService';
import { buildCollapsedTimeline, realToCollapsed, collapsedToReal } from '../gapCollapse';

export default function SensorChartWidget({ recording, recordings = [], sensorKey, events = [] }) {
  const { currentTime, seek, window: timeWindow } = useTimeline();

  // Build collapsed timeline from all recordings in the workspace
  const collapsedTimeline = useMemo(() => {
    const recs = recordings.length > 0
      ? recordings.map((r) => ({ id: r.id, startTime: r.startTime, endTime: r.endTime }))
      : recording
        ? [{ id: recording.id, startTime: recording.startTime, endTime: recording.endTime }]
        : [];
    return buildCollapsedTimeline(recs);
  }, [recordings, recording]);

  const hasCollapsedTimeline = collapsedTimeline.segments.length > 0;

  const chartData = useMemo(() => {
    if (!recording?.data?.[sensorKey]) return null;
    const points = recording.data[sensorKey];

    if (hasCollapsedTimeline) {
      // Map data points to collapsed time space, storing real time for tooltip
      const mappedPoints = points.map((dp) => {
        const collapsed = realToCollapsed(dp.time, collapsedTimeline);
        return { x: collapsed ?? dp.time, y: dp.value, realTime: dp.time };
      }).filter((dp) => dp.x !== null);

      return {
        realTimes: mappedPoints.map((dp) => dp.realTime),
        series: [
          {
            type: 'line',
            name: sensorKey,
            fill: 'solid',
            data: mappedPoints.map((dp) => [dp.x, dp.y]),
          },
        ],
      };
    }

    return {
      labels: points.map((dp) => dp.time),
      series: [
        {
          type: 'line',
          name: friendlyNameConverter(sensorKey),
          fill: 'solid',
          data: points.map((dp) => dp.value),
        },
      ],
    };
  }, [recording, sensorKey, hasCollapsedTimeline, collapsedTimeline]);

  const sensorName = friendlyNameConverter(sensorKey);

  // Build gap indicator annotations (subtle gray dashed lines at each gap boundary)
  const gapAnnotations = useMemo(() => {
    if (!hasCollapsedTimeline) return [];
    return collapsedTimeline.segments
      .filter((seg) => seg.type === 'gap')
      .map((seg) => ({
        x: seg.collapsedStart,
        x2: seg.collapsedEnd,
        fillColor: 'rgba(0,0,0,0.06)',
        opacity: 1,
        label: { text: '' },
      }));
  }, [hasCollapsedTimeline, collapsedTimeline]);

  const eventAnnotations = useMemo(() => {
    if (hasCollapsedTimeline) {
      return events.map((evt) => {
        const x = realToCollapsed(new Date(evt.startTime).getTime(), collapsedTimeline);
        const x2 = realToCollapsed(new Date(evt.endTime).getTime(), collapsedTimeline);
        return x != null && x2 != null
          ? { x, x2, fillColor: '#d50100', label: { text: evt.label } }
          : null;
      }).filter(Boolean);
    }
    return events.map((evt) => ({
      x: new Date(evt.startTime).getTime(),
      x2: new Date(evt.endTime).getTime(),
      fillColor: '#d50100',
      label: { text: evt.label },
    }));
  }, [events, hasCollapsedTimeline, collapsedTimeline]);

  // Map currentTime playhead to collapsed space
  const collapsedCurrentTime = hasCollapsedTimeline
    ? realToCollapsed(currentTime, collapsedTimeline)
    : currentTime;

  // Compute collapsed min/max for x-axis range
  const collapsedMin = hasCollapsedTimeline
    ? (timeWindow?.start != null ? realToCollapsed(timeWindow.start, collapsedTimeline) : 0)
    : timeWindow?.start;
  const collapsedMax = hasCollapsedTimeline
    ? (timeWindow?.end != null ? realToCollapsed(timeWindow.end, collapsedTimeline) : collapsedTimeline.totalCollapsedDuration)
    : timeWindow?.end;

  const chartOptions = useChart({
    plotOptions: { bar: { columnWidth: '16%' } },
    fill: { type: ['solid'] },
    ...(hasCollapsedTimeline ? {} : { labels: chartData?.labels || [] }),
    xaxis: hasCollapsedTimeline
      ? {
          type: 'numeric',
          min: collapsedMin ?? 0,
          max: collapsedMax ?? collapsedTimeline.totalCollapsedDuration,
          labels: {
            formatter: (val) => {
              const realTime = collapsedToReal(val, collapsedTimeline);
              if (realTime == null) return '';
              const d = new Date(realTime);
              return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            },
          },
          tickAmount: 8,
        }
      : {
          type: 'datetime',
          min: timeWindow?.start,
          max: timeWindow?.end,
          labels: { datetimeUTC: false },
        },
    annotations: {
      xaxis: [
        ...gapAnnotations,
        ...eventAnnotations,
        ...(collapsedCurrentTime != null ? [{
          x: collapsedCurrentTime,
          borderColor: '#775DD0',
          label: {
            borderColor: '#775DD0',
            style: { color: '#fff', background: '#775DD0' },
            text: 'Current Time',
          },
        }] : []),
      ],
    },
    chart: {
      toolbar: {
        show: true,
        tools: { download: true, selection: true, pan: true, zoom: true },
      },
      events: {
        markerClick: (_, chartContext, opts) => {
          const index = opts.dataPointIndex;
          if (hasCollapsedTimeline && chartData?.realTimes && index >= 0) {
            seek(chartData.realTimes[index]);
          } else if (chartData?.labels && index >= 0) {
            seek(chartData.labels[index]);
          }
        },
      },
    },
    tooltip: {
      custom: ({ series, dataPointIndex, w }) => {
        const value = series[0]?.[dataPointIndex] ?? 'N/A';
        const color = w.globals.colors[0];
        // Show real time in tooltip, not collapsed time
        let timeStr = '';
        if (hasCollapsedTimeline && chartData?.realTimes?.[dataPointIndex]) {
          const d = new Date(chartData.realTimes[dataPointIndex]);
          timeStr = `<div style="font-size:11px;color:#666;margin-bottom:4px">${d.toLocaleString()}</div>`;
        }
        return `
          <div style="padding: 8px; border-radius: 9px;">
            ${timeStr}
            <div style="display: flex; align-items: center;">
              <span style="background-color: ${color}; border-radius: 50%; width: 12px; height: 12px; margin-right: 8px;"></span>
              <span>${sensorName}:&nbsp;&nbsp;<strong>${value}</strong></span>
            </div>
          </div>
        `;
      },
    },
  });

  if (!chartData) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography color="text.secondary" variant="body2">
          No data for {sensorName}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1, pb: 0, width: '100%', height: '100%' }} dir="ltr">
      <ReactApexChart type="line" series={chartData.series} options={chartOptions} height="100%" />
    </Box>
  );
}
